import { HttpErrorResponse } from '@angular/common/http';
import { IAccount } from '@modeso/types__aumico-lib-annualreport-be';
import { GetTemplatesNamesAndIdsDto, ITemplateEntityDTO, TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import * as fromTemplatesActions from '../actions/templates.actions';
import { IFinancialLineItem } from '../interfaces/settings.interface';

const debug = Debug('aumico:aumico-lib-templates-fe:TemplatesReducer');

export const templateFeatureKey = 'templates';

export interface TemplateState extends EntityState<ITemplateEntityDTO> {
    activeTemplate: string | null;
    activeTemplateType: TemplateType;
    templateByLegalFormAndAccountBasis: ITemplateEntityDTO;
    response: ITemplateEntityDTO;
    error: HttpErrorResponse;
    subtotalsNames: string[];
    financialLineItems: IFinancialLineItem[];
    selectedFinancialLineItems: string[];
    templatesNamesAndIdsByTemplateType: GetTemplatesNamesAndIdsDto[];
    allAccounts: IAccount[];
}

export function selectTemplateId(template: ITemplateEntityDTO): string {
    return template._id;
}

export const adapter: EntityAdapter<ITemplateEntityDTO> = createEntityAdapter<ITemplateEntityDTO>({
    selectId: selectTemplateId,
});

export const { selectEntities, selectAll } = adapter.getSelectors();

export const selectAllTemplates = selectAll;

export const getActiveTemplateId = (state: TemplateState) => state.activeTemplate;
export const getActiveTemplateType = (state: TemplateState) => state.activeTemplateType;
export const getActiveTemplate = createSelector(selectEntities, getActiveTemplateId, (entities, id) => {
    return entities[id] ? Object.assign({}, entities[id]) : entities[id];
});
export const getAllAccounts = (state: TemplateState) => state.allAccounts;
export const getTemplateByLegalFormAndAccountBasis = (state: TemplateState) => state.templateByLegalFormAndAccountBasis;

export const getTemplatesResponse = (state: TemplateState) => state.response;
export const getTemplatesError = (state: TemplateState) => state.error;

export const getAllTemplatesNamesAndIdsByTemplateType = (state: TemplateState) =>
    state.templatesNamesAndIdsByTemplateType;

export const SelectAllTemplatesNamesAndIdsByTemplateType = createSelector(
    getAllTemplatesNamesAndIdsByTemplateType,
    (templatesNamesAndIdsByTemplateType) => {
        return templatesNamesAndIdsByTemplateType;
    },
);

export const getTemplateNameExists = createSelector(
    selectEntities,
    (entities, props: { name: string; templateId: string }) => {
        const keys = Object.keys(entities);
        for (const key of keys) {
            const template: ITemplateEntityDTO = entities[key];
            if (template.name == props.name && template._id != props.templateId) {
                return true;
            }
        }
        return false;
    },
);

export const getTemplateAccountBasisAndLegalFormExists = createSelector(
    selectEntities,
    (entities, props: { accountBasis: string; legalForm: string; type: string; templateId: string }) => {
        const keys = Object.keys(entities);
        for (const key of keys) {
            const template: ITemplateEntityDTO = entities[key];
            if (
                template.accountBasis == props.accountBasis &&
                template.legalForm == props.legalForm &&
                template.type == props.type &&
                template._id != props.templateId
            ) {
                return true;
            }
        }
        return false;
    },
);

export const selectSubtotalsNames = (state: TemplateState) => state.subtotalsNames;
export const getSubtotalNameExists = createSelector(
    selectSubtotalsNames,
    (subtotalsNames: string[], props: { name: string }) => {
        return subtotalsNames.includes(props.name);
    },
);

export const selectAllFinancialLineItems = (state: TemplateState) => state.financialLineItems;
export const getSelectedFinancialLineItems = (state: TemplateState) => state.selectedFinancialLineItems;
export const getFinancialLineItems = createSelector(
    selectAllFinancialLineItems,
    getSelectedFinancialLineItems,
    (financialLineItems: IFinancialLineItem[], selectedFinancialLineItems: string[]) => {
        return financialLineItems
            .filter((fli) => !selectedFinancialLineItems.includes(fli.name))
            .sort(function (a, b) {
                return a.position - b.position;
            });
    },
);
export const getCashAndProfitOptions = createSelector(
    selectAllFinancialLineItems,
    getSelectedFinancialLineItems,
    (financialLineItems: IFinancialLineItem[], selectedFinancialLineItems: string[]) => {
        return financialLineItems.filter((fli) => selectedFinancialLineItems.includes(fli.name)).map((fli) => fli.name);
    },
);

export const initialTemplateState: TemplateState = adapter.getInitialState({
    activeTemplate: null,
    activeTemplateType: null,
    templateByLegalFormAndAccountBasis: undefined,
    response: undefined,
    error: undefined,
    subtotalsNames: [],
    financialLineItems: [],
    selectedFinancialLineItems: [],
    templatesNamesAndIdsByTemplateType: [],
    allAccounts: [],
});

const templatesReducer = createReducer(
    initialTemplateState,

    // Get Templates
    on(fromTemplatesActions.getTemplates, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onGetTemplatesSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, {
            ...state,
        });
    }),
    on(fromTemplatesActions.onGetTemplatesFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Get Template By Legal Form and Account Basis
    on(fromTemplatesActions.getTemplateByLegalFormAndAccountBasis, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onGetTemplateByLegalFormAndAccountBasisSuccessfully, (state, action) => ({
        ...state,
        templateByLegalFormAndAccountBasis: action.payload,
    })),
    on(fromTemplatesActions.onGetTemplateByLegalFormAndAccountBasisFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Active Template
    on(fromTemplatesActions.setActiveTemplate, (state, action) => ({
        ...state,
        activeTemplate: action.payload,
    })),
    on(fromTemplatesActions.setActiveTemplateType, (state, action) => ({
        ...state,
        activeTemplateType: action.payload,
    })),
    on(fromTemplatesActions.removeActiveTemplate, (state) => ({
        ...state,
        activeTemplate: null,
        activeTemplateType: null,
        subtotalsNames: [],
        selectedFinancialLineItems: [],
    })),

    // Create Template
    on(fromTemplatesActions.createTemplate, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onCreateTemplateSuccessfully, (state, action) => {
        return adapter.addOne(action.payload, {
            ...state,
            response: action.payload,
        });
    }),
    on(fromTemplatesActions.onCreateTemplateFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Update Template
    on(fromTemplatesActions.updateTemplate, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onUpdateTemplateSuccessfully, (state, action) => {
        return adapter.updateOne(
            { id: action.payload._id, changes: action.payload },
            {
                ...state,
                response: action.payload,
            },
        );
    }),
    on(fromTemplatesActions.onUpdateTemplateFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Response and error
    on(fromTemplatesActions.removeTemplatesResponse, (state) => ({
        ...state,
        response: undefined,
    })),
    on(fromTemplatesActions.removeTemplatesError, (state) => ({
        ...state,
        error: undefined,
    })),

    // Template Subtotals
    on(fromTemplatesActions.setSubtotalsNames, (state, action) => ({
        ...state,
        subtotalsNames: [...state.subtotalsNames, ...action.payload],
    })),
    on(fromTemplatesActions.removeSubtotalName, (state, action) => ({
        ...state,
        subtotalsNames: [...state.subtotalsNames].filter((name) => name != action.payload),
    })),

    // Template Flis
    on(fromTemplatesActions.setFinancialLineItems, (state, action) => ({
        ...state,
        financialLineItems: action.payload,
    })),
    on(fromTemplatesActions.selectFinancialLineItems, (state, action) => ({
        ...state,
        selectedFinancialLineItems: [...state.selectedFinancialLineItems, ...action.payload],
    })),
    on(fromTemplatesActions.deselectFinancialLineItem, (state, action) => ({
        ...state,
        selectedFinancialLineItems: [...state.selectedFinancialLineItems].filter((fli) => fli != action.payload),
    })),

    on(fromTemplatesActions.resetTemplateByLegalFormAndAccountBasis, (state) => ({
        ...state,
        templateByLegalFormAndAccountBasis: null,
    })),

    on(
        fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateType,
        (state) => ({
            ...state,
            error: undefined,
        }),
    ),
    on(
        fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateTypeSuccess,
        (state, action) => ({
            ...state,
            templatesNamesAndIdsByTemplateType: action.payload,
            error: undefined,
        }),
    ),
    on(
        fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateTypeFailed,
        (state, action) => ({
            ...state,
            templatesNamesAndIdsByTemplateType: null,
            error: action.payload,
        }),
    ),

    on(fromTemplatesActions.allAccounts.set, (state, action) => ({
        ...state,
        allAccounts: action.payload,
    })),
    on(fromTemplatesActions.allAccounts.addaccount, (state, action) => ({
        ...state,
        allAccounts: [...state.allAccounts, action.payload],
    })),
    on(fromTemplatesActions.allAccounts.updateAccount, (state, action) => {
        const { oldAccountNumber } = action.payload;
        const updatedAccountIndex = state.allAccounts.findIndex(
            (account) => account.accountNumber === oldAccountNumber,
        );
        const arrayWithUpdatedAccount = [...state.allAccounts];
        arrayWithUpdatedAccount[updatedAccountIndex] = action.payload.updatedAccount;
        return {
            ...state,
            allAccounts: [...arrayWithUpdatedAccount],
        };
    }),
);

export function reducer(state: TemplateState | undefined, action: Action) {
    return templatesReducer(state, action);
}
